import { useEffect, useCallback } from 'react';

/**
 * FR-171: Module-level flag to synchronously bypass navigation guard.
 * This is NOT React state because React state updates are async and won't
 * take effect before the beforeunload event fires during window.location.replace().
 */
let bypassNavigationGuard = false;

/**
 * FR-171: Set the bypass flag synchronously before redirecting.
 * Call this BEFORE window.location.replace() to prevent "Leave site?" dialog
 * during session expiration or error redirects.
 */
export function setBypassNavigationGuard(value: boolean): void {
  bypassNavigationGuard = value;
}

/**
 * T132-T134: Hook to prevent accidental navigation away from a page with unsaved work
 *
 * Uses the browser's beforeunload event to show a native confirmation dialog
 * when the user tries to:
 * - Close the tab
 * - Navigate to a different URL
 * - Click the browser back button
 * - Close the browser
 *
 * @param shouldBlock - Whether navigation should be blocked (e.g., active session exists)
 * @param onBeforeUnload - Optional callback to run before showing the dialog
 *
 * FR-067: Register beforeunload event listener when active session exists
 * FR-068: Browser displays native "Leave site?" dialog
 * FR-072: Listener only active when session is active
 * FR-073: Listener removed when session ends naturally
 * FR-171: Check synchronous bypass flag before blocking (for session expiration redirects)
 */
export function useNavigationGuard(
  shouldBlock: boolean,
  onBeforeUnload?: () => void
): void {
  // T133: Handle beforeunload event
  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      // FR-171: Check synchronous bypass flag first (for session expiration redirects)
      if (bypassNavigationGuard || !shouldBlock) return;

      // Call optional callback before showing dialog
      if (onBeforeUnload) {
        onBeforeUnload();
      }

      // Modern browsers ignore custom messages for security reasons
      // They show their own generic "Leave site?" message
      e.preventDefault();
      // For older browsers, setting returnValue is required
      e.returnValue = '';
      return '';
    },
    [shouldBlock, onBeforeUnload]
  );

  // T133 & T134: Add/remove event listener based on shouldBlock state
  useEffect(() => {
    if (!shouldBlock) {
      // T134: Remove listener when session ends (shouldBlock becomes false)
      return;
    }

    // T133: Add beforeunload listener when session is active
    window.addEventListener('beforeunload', handleBeforeUnload);

    // T134: Cleanup - remove listener when component unmounts or shouldBlock changes
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, handleBeforeUnload]);
}

export default useNavigationGuard;
